import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAccount } from 'wagmi';

import {
  AuthActions,
  AuthState,
  useAuthActions,
  useAuthState,
} from '#/features/auth/auth-context';
import { useSystemConfig } from '#/features/system/system-config-context';
import { EthereumAddress } from '#/features/ethereum-wallet/wallet';

import createSafeContext from '#/utils/createSafeContext';
import { AsyncResult } from '#/utils/types';

import actionPerformAuth from './wallet-context/action-perform-auth';
import actionConnectWallet from './wallet-context/action-connect-wallet';
import actionInitializeStarknetWallet from './wallet-context/action-initialize-starknet-wallet';
import actionPerformOnboarding from './wallet-context/action-perform-onboarding';
import { INITIAL_STATE_WALLET_ONBOARDED } from './wallet-context-mock';

export interface WalletState {
  readonly step:
    | 'wallet_not_connected'
    | 'confirming_wallet_connection'
    | 'wallet_connected_idle'
    | 'signing_stark_keys'
    | 'signing_onboarding'
    | 'signing_auth_request';
  readonly kind: 'metamask';
  readonly ethereumAddress: EthereumAddress | null;
  readonly starknetAddress: string | null;
  readonly isOnboarded: boolean;
  readonly error: string;
}

export interface WalletView {
  readonly ethereumAddress: WalletState['ethereumAddress'];
  readonly starknetAddress: WalletState['starknetAddress'];
  readonly error: WalletState['error'];

  // Derived:
  readonly nextSignInStep:
    | 'wallet_connection'
    | 'onboarding_and_authentication'
    | null; // `null` if auth process is complete and no more steps required
  readonly isWalletConnected: boolean;
  readonly isSignedIn: boolean;
  readonly status: 'idle' | 'progress' | 'complete';
}

const INITIAL_STATE: WalletState =
  IS_RUNNING_IN_CYPRESS === 'true'
    ? INITIAL_STATE_WALLET_ONBOARDED
    : {
        step: 'wallet_not_connected',
        kind: 'metamask',
        ethereumAddress: null,
        starknetAddress: null,
        isOnboarded: false,
        error: '',
      };

interface WalletActions {
  readonly connectWallet: () => AsyncResult<void>;
  readonly initializeStarknetWallet: () => AsyncResult<void>;
  readonly performOnboarding: () => AsyncResult<void>;
  readonly performAuth: () => AsyncResult<void>;
  readonly resetError: () => void;
  readonly reset: () => void;
}

export const [useWalletState, WalletStateCtx] =
  createSafeContext<[WalletState, Dispatch<SetStateAction<WalletState>>]>(
    'WalletState',
  );
export const [useWalletView, WalletViewCtx] =
  createSafeContext<WalletView>('WalletView');
export const [useWalletActions, WalletActionsCtx] =
  createSafeContext<WalletActions>('WalletActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function WalletStateProvider(props: ProviderProps) {
  const stateTuple = useState(INITIAL_STATE);
  let { address: ethereumAddress } = useAccount();

  if (IS_RUNNING_IN_CYPRESS === 'true') {
    ethereumAddress = INITIAL_STATE_WALLET_ONBOARDED.ethereumAddress;
  }

  const [_, setState] = stateTuple;
  useEffect(() => {
    setState((state) => ({
      ...state,
      ethereumAddress: ethereumAddress ?? null,
    }));
  }, [ethereumAddress, setState]);

  return <WalletStateCtx value={stateTuple}>{props.children}</WalletStateCtx>;
}

export function WalletActionsProvider(props: ProviderProps) {
  const [walletState, setWalletState] = useWalletState();
  const [authState] = useAuthState();
  const authActions = useAuthActions();
  const config = useSystemConfig();
  const options = useMemo(
    () => ({
      ethereumChainId: config.ethereum.chainId,
      starknetChainId: config.starknet.chainId,
      accountClassHash: config.starknet.accountClassHash,
      accountProxyClassHash: config.starknet.accountProxyClassHash,
    }),
    [
      config.ethereum.chainId,
      config.starknet.accountClassHash,
      config.starknet.accountProxyClassHash,
      config.starknet.chainId,
    ],
  );

  const actions = useMemo(
    () =>
      createActions(
        walletState,
        setWalletState,
        authState,
        authActions,
        options,
      ),
    [walletState, setWalletState, authState, authActions, options],
  );

  return <WalletActionsCtx value={actions}>{props.children}</WalletActionsCtx>;
}

export function WalletViewProvider(props: ProviderProps) {
  const [walletState] = useWalletState();
  const [authState] = useAuthState();

  const view = useMemo(
    () => createView(walletState, authState),
    [walletState, authState],
  );

  return <WalletViewCtx value={view}>{props.children}</WalletViewCtx>;
}

function createView(
  walletState: WalletState,
  authState: AuthState,
): WalletView {
  const isWalletConnected = isWalletConnectedView(walletState);
  const nextSignInStep = nextSignInStepView(walletState, authState);
  const isSignedIn = nextSignInStep === null;
  const status = statusView(walletState, authState);

  return {
    ethereumAddress: walletState.ethereumAddress,
    starknetAddress: walletState.starknetAddress,
    error: walletState.error,
    nextSignInStep,
    isWalletConnected,
    isSignedIn,
    status,
  };
}

function nextSignInStepView(
  walletState: WalletState,
  authState: AuthState,
): WalletView['nextSignInStep'] {
  if (walletState.ethereumAddress === null) return 'wallet_connection';
  if (walletState.starknetAddress === null) return 'wallet_connection';
  if (!walletState.isOnboarded) return 'onboarding_and_authentication';
  if (authState.token == null) return 'onboarding_and_authentication';
  return null;
}

function isWalletConnectedView(
  walletState: WalletState,
): WalletView['isWalletConnected'] {
  if (walletState.ethereumAddress == null) return false;
  return true;
}

function statusView(
  state: WalletState,
  authState: AuthState,
): WalletView['status'] {
  switch (state.step) {
    case 'wallet_not_connected':
      return 'idle';
    case 'confirming_wallet_connection':
    case 'signing_stark_keys':
    case 'signing_onboarding':
    case 'signing_auth_request':
      return 'progress';
    case 'wallet_connected_idle':
      return state.isOnboarded && authState.token != null ? 'complete' : 'idle';
    // no default
  }
}

export interface EthereumChainConfigurable {
  readonly ethereumChainId: number;
}

export interface StarknetChainConfigurable {
  readonly starknetChainId: string;
}

export interface AccountContractConfigurable {
  readonly accountClassHash: string;
  readonly accountProxyClassHash: string;
}

interface CreateActionsOptions
  extends EthereumChainConfigurable,
    StarknetChainConfigurable,
    AccountContractConfigurable {}

function createActions(
  walletState: WalletState,
  setWalletState: Dispatch<SetStateAction<WalletState>>,
  authState: AuthState,
  authActions: AuthActions,
  options: CreateActionsOptions,
): WalletActions {
  const reset = () => {
    setWalletState(INITIAL_STATE);
  };

  const resetError = () => {
    setWalletState((state) => ({ ...state, error: '' }));
  };

  const connectWallet = actionConnectWallet(setWalletState, {
    ethereumChainId: options.ethereumChainId,
  });

  const initializeStarknetWallet = actionInitializeStarknetWallet(
    authState,
    authActions,
    setWalletState,
    {
      ethereumChainId: options.ethereumChainId,
      accountClassHash: options.accountClassHash,
      accountProxyClassHash: options.accountProxyClassHash,
    },
  );

  const performOnboarding = actionPerformOnboarding(
    walletState,
    setWalletState,
    { starknetChainId: options.starknetChainId },
  );

  const performAuth = actionPerformAuth(authActions, setWalletState);

  return {
    connectWallet,
    initializeStarknetWallet,
    performOnboarding,
    performAuth,
    resetError,
    reset,
  };
}
